/**
 * EventRouteMap — the course, drawn across the full width of the card.
 *
 * Shown only when there is a real line to draw (the organiser's GPX file), and
 * it earns the width: a route is a shape, and a shape squeezed into a third of
 * a card is a smudge. It sits UNDER the facts rather than replacing any of
 * them — the small map beside each address answers "where does it start?" at a
 * glance, which is a different question from "what is the course?".
 *
 * The inline map does not respond to the wheel. A map inside a scrolling page
 * that swallows the wheel is a trap: the page stops and the map zooms instead.
 * Anyone who wants to look closer opens the maximised view, where zooming is
 * the expected thing to do and there is nothing behind it to scroll.
 */

import React, { useEffect, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const START_ICON = L.divIcon({
  className: 'evb-mappin',
  html: '<span class="evb-mappin__dot evb-mappin__dot--start" aria-hidden="true"></span>',
  iconSize: [20, 20], iconAnchor: [10, 10],
});
const END_ICON = L.divIcon({
  className: 'evb-mappin',
  html: '<span class="evb-mappin__dot evb-mappin__dot--end" aria-hidden="true"></span>',
  iconSize: [20, 20], iconAnchor: [10, 10],
});

/** The window is the whole route, whatever size the box happens to be. */
function FitToRoute({ line, deps }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !line || line.length < 2) return;
    if (typeof map.fitBounds !== 'function') return;
    map.fitBounds(line.map(p => [p.lat, p.lng]), { padding: [24, 24] });
    // A map that was hidden when it mounted measures itself as 0×0 and draws
    // one grey tile. The maximised view is exactly that case.
    if (typeof map.invalidateSize === 'function') map.invalidateSize();
  }, [map, line, deps]);

  return null;
}

function RouteCanvas({ line, startLabel, endLabel, interactive }) {
  const first = line[0];
  const last = line[line.length - 1];

  return (
    <MapContainer
      className="evb-routemap__canvas"
      center={[first.lat, first.lng]}
      zoom={13}
      dragging={interactive}
      scrollWheelZoom={interactive}
      doubleClickZoom={interactive}
      touchZoom={interactive}
      keyboard={interactive}
      zoomControl={interactive}
      attributionControl={false}
    >
      <TileLayer
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        attribution="&copy; OpenStreetMap contributors"
      />
      <Polyline
        positions={line.map(p => [p.lat, p.lng])}
        pathOptions={{ color: '#12666C', weight: 4, opacity: 0.9 }}
      />
      <Marker position={[first.lat, first.lng]} icon={START_ICON}>
        {startLabel && <Tooltip direction="top" offset={[0, -10]}>{startLabel}</Tooltip>}
      </Marker>
      <Marker position={[last.lat, last.lng]} icon={END_ICON}>
        {endLabel && <Tooltip direction="top" offset={[0, -10]}>{endLabel}</Tooltip>}
      </Marker>
      <FitToRoute line={line} deps={interactive} />
    </MapContainer>
  );
}

export default function EventRouteMap({ line, startLabel, endLabel, title }) {
  const [open, setOpen] = useState(false);

  // Escape closes it, as it does every other overlay. Bound only while open, so
  // nothing listens for a key that cannot do anything.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = event => { if (event.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!line || line.length < 2) return null;

  return (
    <div className="evb-routemap">
      <div className="evb-routemap__head">
        <span className="evb-routemap__k">The course</span>
        <button type="button" className="evb-routemap__max" onClick={() => setOpen(true)}>
          Maximise
        </button>
      </div>

      <RouteCanvas line={line} startLabel={startLabel} endLabel={endLabel} interactive={false} />
      <p className="evb-map__credit">© OpenStreetMap contributors</p>

      {open && (
        <div
          className="evb-routemax"
          role="dialog"
          aria-modal="true"
          aria-label={title || 'The course'}
          // The backdrop closes it; the panel stops the click so a drag that
          // ends outside the map does not dismiss the thing being dragged.
          onClick={() => setOpen(false)}
        >
          <div className="evb-routemax__panel" onClick={event => event.stopPropagation()}>
            <div className="evb-routemax__head">
              <span>{title || 'The course'}</span>
              <button type="button" className="evb-routemax__close" onClick={() => setOpen(false)}>
                Close
              </button>
            </div>
            <RouteCanvas line={line} startLabel={startLabel} endLabel={endLabel} interactive />
            <p className="evb-routemax__foot">
              Scroll to zoom, drag to pan · © OpenStreetMap contributors
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
